import React, {useContext, useEffect, useMemo, useRef, useState} from "react";

import {Trans, useTranslation} from "react-i18next";
import {
  Button,
  Col,
  Container,
  Form,
  FormCheck,
  FormControl,
  FormLabel,
  InputGroup,
  Row,
  Table,
} from "react-bootstrap";
import emitter from "eventBus";
import ActionModal from "../components/Modals/ActionModals/ActionModal";
import {faPlus,} from "@fortawesome/free-solid-svg-icons";

import ClaimGuard from "../components/Auth/ClaimGuard";
import {
  ADJUST_BALANCE,
  GET_BILLING_REPORT,
  GET_BUNDLES,
  GET_BUNDLES_ALL,
  TOP_UP_BALANCE,
  UPDATE_BILLING_ALL,
  UPDATE_BUNDLES_ALL,
} from "../Claims";
import useClaims from "../Hooks/useClaims";

import {toast} from "react-toastify";
import messageFromError from "../utils/errorUtils";
import useCompanyId from "../Hooks/useCompanyId";
import useEmail from "../Hooks/useEmail";

import {
  adjustBalance,
  bundleOperationsForCompany,
  getAllBundles,
  getBalanceForCompany,
  getBillingInfo,
  getBundlesForCompany,
  topUpCompanyBalance,
  updateBillingInfo,
} from "../API/companyApi";
import ManagementCharts from "components/ManagementCharts";
import PurchaseTable from "components/PurchaseTable";
import calculateCreditsOrPercentage from "../utils/creditsUtils.tsx";
import IconButton from "../components/IconButton";
import {usePrompt} from "Hooks/usePrompt.tsx";
import ConditionalRender from "../components/ConditionalRender";
import {SalvoContext} from "../contexts/SalvoContext";

const CreditManagementPage = () => {
  const { t } = useTranslation();
  const companyId = useCompanyId();
  const email = useEmail();
  const { hasClaim } = useClaims();
  const { setBillingInfo } = useContext(SalvoContext);

  const [balance, setBalance] = useState(0);
  const [action, setAction] = useState("");
  const [currentBalance, setCurrentBalance] = useState(0);

  const [availableBundles, setAvailableBundles] = useState([]);
  const [bundleIds, setBundleIds] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selectedBundleId, setSelectedBundleId] = useState(null);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [autoRenewThreshold, setAutoRenewThreshold] = useState(null);
  const [defaultBundleId, setDefaultBundleId] = useState(null);
  const [defaultTemporaryBundleId, setDefaultTemporaryBundleId] = useState(null);

  const [showAddInput, setShowAddInput] = useState(false);
  const [newBundleLabel, setNewBundleLabel] = useState("");
  const [increaseBalanceNote, setIncreaseBalanceNote] = useState("");
  const [newNote, setNewNote] = useState("");
  const [bundleToDelete, setBundleToDelete] = useState(null);
  const [bundleToToggle, setBundleToToggle] = useState(null);

  const [adjustedBalance, setAdjustedBalance] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [showTopUpConfirmation, setShowTopUpConfirmation] = useState(false);

  async function fetchBalance() {
    try {
      const response = await getBalanceForCompany(companyId);
      setBalance(response.data.balance);
      setCurrentBalance(response.data.balance);
    } catch (e) {
      toast.error(messageFromError(e));
    }
  }

  async function fetchAvailableBundlesForCompany() {
    try {
      const response = await getBundlesForCompany(companyId);
      setAvailableBundles(response.data);
    } catch (e) {
      toast.error(messageFromError(e));
    }
  }

  async function fetchBundles() {
    try {
      var fetchAll = false;
      if(hasClaim(GET_BUNDLES_ALL)){
        fetchAll = true
      }
      const response = await getAllBundles(companyId, fetchAll);
      setBundles(response.data);
      fetchBillingInfo(response.data);
    } catch (e) {
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  async function topUpBalanceForCompany() {
    try {
      await topUpCompanyBalance(companyId, selectedBundleId, {
        issuer: email,
        type: "manual",
      });
      toast.success(t("toast.success.topUpBalance"));
      emitter.emit("refresh-balance");
      fetchBalance();
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  // TODO which of those settings are super admin managed and which managed by Admin
  async function fetchBillingInfo(bundless) {
    try {
      const response = await getBillingInfo(companyId);
      const data = response.data;
      if (data.billingEnabled) {
        const defaultBundleCredits = getCreditsForBundle(
          data.defaultBundleId,
          bundless
        );
        if (defaultBundleCredits == null) {
          setBillingEnabled(data.billingEnabled);
          setAutoRenew(data.autoRenewEnabled);
          setAutoRenewThreshold(null);
          setDefaultBundleId(data.defaultBundleId);
          return;
        }
        var percentage = Number(
          calculateCreditsOrPercentage({
            credits: data.autoRenewThreshold,
            defaultCredits: defaultBundleCredits,
          })
        ).toFixed(0);
        setBillingEnabled(data.billingEnabled);
        setAutoRenew(data.autoRenewEnabled);
        setAutoRenewThreshold(percentage);
        setDefaultBundleId(data.defaultBundleId);
      } else {
        setBillingEnabled(data.billingEnabled);
      }
    } catch (e) {
      console.log("errr ", e);
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  async function patchBillingInfo() {
    try {
      const defaultBundleCredits = getCreditsForBundle(
        defaultBundleId,
        bundles
      );
      const credits = calculateCreditsOrPercentage({
        percentage: autoRenewThreshold,
        defaultCredits: defaultBundleCredits,
      });
      const response = await updateBillingInfo(companyId, {
        autoRenewEnabled: autoRenew,
        autoRenewThreshold: credits,
        billingEnabled: billingEnabled,
        defaultBundleId: defaultBundleId,
      });

      setBillingInfo(response.data);

      // console.log("billingg ", billingEnabled);

      toast.success(t("toast.success.updateSettings"));

      initialValuesRef.current = {
        autoRenew,
        autoRenewThreshold,
        adjustedBalance,
        increaseBalanceNote,
      };

      // Optionally, reset the loaded flag so isFormDirty recalculates:
      setDataLoaded(false);
      setTimeout(() => setDataLoaded(true), 0);
      // setBundles(response.data);
    } catch (e) {
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  async function adjustBalanceInfo() {
    try {
      const response = await adjustBalance(companyId, {
        amount: Number(adjustedBalance),
        issuer: email,
        note: increaseBalanceNote,
      });
      setAdjustedBalance('');
      setIncreaseBalanceNote('');
      fetchBalance();
      emitter.emit("refresh-balance");
      toast.success(t("toast.success.updateBalance"));
      initialValuesRef.current = {
        autoRenew,
        autoRenewThreshold,
        adjustedBalance,
        increaseBalanceNote,
      };

      // Optionally, reset the loaded flag so isFormDirty recalculates:
      setDataLoaded(false);
      setTimeout(() => setDataLoaded(true), 0);

    // ✅ Trigger refresh for child components
    setRefreshKey(prev => prev + 1);
      // setBundles(response.data);
    } catch (e) {
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  async function updateBundleVisibilityForCompany(updatedBundles) {
    try {
      await bundleOperationsForCompany(companyId, updatedBundles);
      // toast.success("Bundles updated successfully!");
      await fetchAvailableBundlesForCompany();
      await fetchBundles();
    } catch (e) {
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  async function createBundleForCompany(updatedBundles) {
    try {
      await bundleOperationsForCompany(companyId, updatedBundles);
      toast.success(t("toast.success.bundleCreated"));
      await fetchAvailableBundlesForCompany();
      await fetchBundles();
    } catch (e) {
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  async function deleteBundleForCompany(updatedBundles) {
    try {
      await bundleOperationsForCompany(companyId, updatedBundles);
      toast.success(t("toast.success.bundleDeleted"));
      await fetchAvailableBundlesForCompany();
      await fetchBundles();
    } catch (e) {
      console.log("errrr ", e);
      toast.error(messageFromError(e) || t("toast.error.generic"));
    }
  }

  const defaultBundleOptions = availableBundles.map((bundle) => ({
    value: bundle.id,
    label: `${bundle.amount} Credits`,
  }));

  const bundleOptions = bundles.map((bundle) => ({
    value: bundle.id,
    label: `${bundle.amount} Credits`,
    note: bundle.note,
  }));

  const selectedBundleOptions = bundleOptions.filter((option) =>
    bundleIds.includes(option.value)
  );

  function getCreditsForBundle(defaultBundleId, bundles) {
    const bundle = bundles.find((b) => b.id === defaultBundleId && b.active);
    return bundle ? bundle.amount : null; // return null if not found or inactive
  }

  useEffect(() => {
    async function fetchData() {
      await fetchBundles();
      await fetchBalance();
      await fetchAvailableBundlesForCompany();
      // await fetchBillingInfo();
    }

    fetchData();
  }, []);

  useEffect(() => {
    // When available bundles are fetched, set them as initial selected bundles
    if (availableBundles.length > 0) {
      const ids = availableBundles.map((b) => b.id);
      setBundleIds(ids);
    }
  }, [availableBundles]);

  const [showConfirm, setShowConfirm] = useState(false);
  const [showConfirmNewDefaultBundle, setShowConfirmNewDefaultBundle] =
    useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  const [originalBillingEnabled, setOriginalBillingEnabled] = useState(null);

  const handleSwitchChange = (e) => {
    const newValue = e.target.checked;
    setOriginalBillingEnabled(billingEnabled);
    setBillingEnabled(newValue);
    setPendingValue(newValue);   
    setShowConfirm(true);
  };

  const onConfirmChange = async () => {
    try {
      await patchBillingInfo();
    } catch (error) {
      console.error("Failed to patch billing info:", error);
    }
    setShowConfirm(false);
    setOriginalBillingEnabled(null);
  };

  const onCancelChange = () => {
    setShowConfirm(false);
    setBillingEnabled(originalBillingEnabled);
    setOriginalBillingEnabled(null);
  };

  const initialValuesRef = useRef(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // When all relevant data is loaded, save initial values once
  useEffect(() => {
    if (
      defaultBundleId !== null && // or any other necessary condition to ensure data loaded
      initialValuesRef.current === null
    ) {
      initialValuesRef.current = {
        autoRenew,
        autoRenewThreshold,
        adjustedBalance,
        increaseBalanceNote,
      };
      setDataLoaded(true);
    }
  }, [
    autoRenew,
    autoRenewThreshold,
    adjustedBalance,
    increaseBalanceNote,
    defaultBundleId,
  ]);

  const isFormDirty = useMemo(() => {
    if (!dataLoaded || !initialValuesRef.current) return false;
    const initial = initialValuesRef.current;

    return (
      autoRenew !== initial.autoRenew ||
      autoRenewThreshold !== initial.autoRenewThreshold ||
      adjustedBalance !== initial.adjustedBalance ||
      (increaseBalanceNote?.trim() || "") !== (initial.increaseBalanceNote?.trim() || "")
    );
  }, [autoRenew, autoRenewThreshold, adjustedBalance, increaseBalanceNote, dataLoaded]);

  // Hook to prompt on exit
  const exitPrompt = usePrompt(
    "You have unsaved changes. Are you sure you want to leave?",
    isFormDirty
  );

  return (
    <div className="credit-management-page ">
      {exitPrompt}

      <Container>
        <h1 className={"mb-0"}>
          {t("pages.creditManagement.title")}
        </h1>
        <div>
          <h5>
            {t("creditManagement.balance")} {balance}
          </h5>
        </div>
        <ClaimGuard requiredClaim={UPDATE_BILLING_ALL}>
          <Row className={'mt-3'}>
            <Col>
              <Form.Group className="mb-3 mt-3 d-flex align-items-center gap-2">
                <FormCheck
                    type="switch"
                    id="billingEnabled"
                    checked={billingEnabled}
                    onChange={handleSwitchChange}
                />
                <Form.Label htmlFor="billingEnabled" className="mb-0">
                  {t("dialogs.billingEnabled")}
                </Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <div className={'border border-primary rounded px-3 py-3'}>
            <fieldset disabled={!billingEnabled} style={{opacity: !billingEnabled ? 0.6 : 1}}>
              <Row>
                <Col lg={2}>
                  <Form.Label htmlFor="autoRenewEnabled" className="mb-0">
                    {t("dialogs.autoRenew")}
                  </Form.Label>
                  <FormCheck
                      type="switch"
                      id="autoRenewEnabled"
                      checked={autoRenew}
                      onChange={(e) => setAutoRenew(e.target.checked)}
                  />
                </Col>
                <Col>
                  <InputGroup className="mb-2" style={{ maxWidth: "200px" }}>
                    <Form.Label
                        htmlFor="autoRenewThreshold"
                        className="form-label me-2"
                    >
                      {t("dialogs.autoRenewThreshold")}
                    </Form.Label>
                    <Form.Control
                        type="number"
                        id="autoRenewThreshold"
                        min="0"
                        max={300}
                        value={autoRenewThreshold}
                        placeholder="0"
                        onChange={(e) => {
                          const value = e.target.value;
                          setAutoRenewThreshold(value === "" ? "" : Number(value));
                        }}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Col>
              </Row>
              <Row className={'mt-2'}>
                <Col>
                  <Button
                      variant={'primary'}
                      onClick={patchBillingInfo}>
                    {t("creditManagement.setSettings")}
                  </Button>
                </Col>
              </Row>
            </fieldset>
          </div>
        </ClaimGuard>
        
        <fieldset
            disabled={!billingEnabled}
            style={{opacity: !billingEnabled ? 0.6 : 1}}>
          <ClaimGuard requiredClaim={ADJUST_BALANCE}>
            <div className={'mt-4 border border-primary rounded px-3 py-3'}>
              <Row>
                <Row>
                  <Col lg={4}>
                    {/* <div className="d-flex align-items-center mb-3 gap-2"> */}
                    <Form.Group className="mb-3">
                      <FormLabel>{t("creditManagement.adjustBalance")}</FormLabel>
                      <FormControl
                          type="number"
                          placeholder={"0"}
                          style={{resize: "none"}}
                          value={adjustedBalance}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAdjustedBalance(val === "" ? "" : Number(val));
                          }}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={8}>
                    <Form.Group className="mb-3">
                      <FormLabel>{t("creditManagement.note")}</FormLabel>
                      <FormControl
                          type="text"
                          placeholder={t("creditManagement.note")}
                          style={{resize: "none"}}
                          value={increaseBalanceNote}
                          onChange={(e) => {
                            setIncreaseBalanceNote(e.target.value);
                          }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className={'d-flex align-items-center'}>
                      <Button
                          variant={'primary'}
                          onClick={() => {
                            adjustBalanceInfo();
                          }}
                      >
                        {t("creditManagement.adjustBalanceButton")}
                      </Button>
                      {typeof adjustedBalance === "number" &&
                          !isNaN(adjustedBalance) && (
                              <div className="ms-3 text-muted">
                                {t("creditManagement.newBalancePreview")}{" "}
                                <strong>
                                  {(currentBalance + adjustedBalance).toFixed(2)}
                                </strong>
                              </div>
                          )}
                    </div>
                  </Col>
                </Row>
              </Row>
            </div>
          </ClaimGuard>

          <Row className="mt-4">
            <ClaimGuard requiredClaim={GET_BUNDLES}>
              <Row>
                <Col>
                  <div className="position-relative mb-3">
                    <ConditionalRender condition={hasClaim(UPDATE_BUNDLES_ALL)}>
                      <div className="d-flex justify-content-between align-items-center mb-2 fw-bold">
                        <span>{t("creditManagement.availableBundles")}</span>
                        <IconButton
                            icon={faPlus}
                            color="white"
                            bg="#198754"
                            onClick={() => setShowAddInput(true)}
                        />
                      </div>
                    </ConditionalRender>
                    <Table bordered hover className="mb-3">
                      <thead>
                      <tr>
                        <th style={{width: "40%", whiteSpace: "nowrap"}}>
                          {t("creditManagement.bundle")}
                        </th>
                        <ClaimGuard requiredClaim={UPDATE_BUNDLES_ALL}>
                        <th style={{width: "30%", whiteSpace: "nowrap"}}>
                          {t("creditManagement.note")}
                        </th>
                        <th style={{textAlign: "center"}}>
                          {t("creditManagement.default")}
                        </th>

                        {/* Only show if user has UPDATE_BUNDLES_ALL */}
                        <th style={{textAlign: "center"}}>
                          {t("creditManagement.visible")}
                        </th>
                        <th style={{textAlign: "center"}}>
                          {t("creditManagement.actions")}
                        </th>
                      </ClaimGuard>
                    </tr>
                    </thead>

                    <tbody>
                    {(hasClaim(UPDATE_BUNDLES_ALL)
                            ? bundleOptions
                            : defaultBundleOptions
                    ).map((bundle) => {
                      const isDefault = defaultBundleId === bundle.value;
                      return (
                          <tr
                              key={bundle.value}
                              onClick={() => setSelectedBundleId(bundle.value)}
                              className={
                                selectedBundleId === bundle.value
                                    ? "table-active"
                                    : ""
                              }
                              style={{
                                cursor: "pointer",
                                backgroundColor:
                                    selectedBundleId === bundle.value
                                        ? "#d1ecf1"
                                        : "transparent",
                              }}
                          >
                            <td>{bundle.label}</td>
                            <ClaimGuard requiredClaim={UPDATE_BUNDLES_ALL}>
                              <td>{bundle.note || "-"}</td>

                              <td style={{textAlign: "center"}}>
                                <input
                                    type="radio"
                                    name="defaultBundle"
                                    checked={isDefault}
                                    onChange={() => {
                                      setDefaultTemporaryBundleId(
                                          defaultBundleId
                                      );
                                      setDefaultBundleId(bundle.value);
                                      setShowConfirmNewDefaultBundle(true);
                                      setAction("setDefaultBundle");
                                    }}
                                />
                              </td>

                              <td
                                  style={{
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                  }}
                              >
                                <input
                                    type="checkbox"
                                    checked={bundleIds.includes(bundle.value)}
                                    onChange={(e) => {
                                      setBundleToToggle({
                                        bundle,
                                        active: e.target.checked,
                                      });
                                      setAction("toggleBundleVisibility");
                                    }}
                                />
                              </td>
                              <td
                                  style={{
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                  }}
                              >
                                {!isDefault && (
                                    <i
                                        className="fas fa-trash-alt text-danger"
                                        style={{cursor: "pointer"}}
                                        onClick={() => {
                                          setBundleToDelete(bundle);
                                          setAction("deleteBundle");
                                        }}
                                    />
                                )}
                              </td>
                            </ClaimGuard>
                          </tr>
                      );
                    })}
                    </tbody>
                  </Table>

                  {showAddInput && (
                      <div className="d-flex align-items-center mb-3 gap-2">
                        <input
                            type="text"
                            value={newBundleLabel}
                            onChange={(e) => setNewBundleLabel(e.target.value)}
                            className="form-control"
                            placeholder={t("creditManagement.enterCredits")}
                        />
                        <input
                            type="text"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            className="form-control ms-1"
                            placeholder={t("creditManagement.enterNote")}
                        />
                        <i
                            className="fas fa-plus text-success"
                            style={{cursor: "pointer", fontSize: "1.25rem"}}
                            onClick={() => {
                              if (newBundleLabel.trim()) {
                                const newBundle = {
                                  amount: parseInt(newBundleLabel.trim(), 10),
                                  active: true,
                                };

                                if (newNote.trim()) {
                                  newBundle.note = newNote.trim();
                                }

                                const updatedBundles = [...bundles, newBundle];

                                setBundles(updatedBundles);
                                setNewBundleLabel("");
                                setNewNote("");
                                setShowAddInput(false);

                                // Send to API (replace 'companyId' with the actual ID)
                                createBundleForCompany(updatedBundles);
                              }
                            }}
                        ></i>
                      </div>
                  )}

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <ClaimGuard requiredClaim={TOP_UP_BALANCE}>
                      <button
                          disabled={!selectedBundleId}
                          onClick={() => setShowTopUpConfirmation(true)}
                          className="btn btn-primary"
                      >
                        {t("creditManagement.topUp")}
                      </button>
                    </ClaimGuard>
                  </div>
                  </div>
                </Col>
              </Row>
            </ClaimGuard>
          </Row>

          {/* <ClaimGuard requiredClaim={GET_SETTINGS}> */}
          <ClaimGuard requiredClaim={GET_BILLING_REPORT}>
            <Row>
              <PurchaseTable companyId={companyId} type={"adjusted_balance"} refreshTrigger={refreshKey} />
              <PurchaseTable companyId={companyId} type={"purchase_table"} refreshTrigger={refreshKey} />

              <ManagementCharts
                  companyId={companyId}
                  graphName={t("creditManagement.balanceHistoryGraph")}
                  type="balance"
                  balance={balance}
              />

              <ManagementCharts
                  companyId={companyId}
                  graphName={t("creditManagement.msisdnConsumptionGraph")}
                  type="msisdnConsumption"
              />
            </Row>
          </ClaimGuard>
        </fieldset>
        {/* </ClaimGuard> */}
        {/* <ClaimGuard requiredClaim={GET_BILLING_REPORT_ALL}>
          <div className="mt-5">
            <ExportAllDataButton companyId={companyId} />
          </div>
        </ClaimGuard> */}
      </Container>

      <ActionModal
          func={async () => {
            if (!bundleToDelete) return;

            const updatedOptions = bundleOptions.filter(
            (opt) => opt.value !== bundleToDelete.value
          );

          const updatedBundlesRaw = bundles.filter((b) => {
            if (b.id) return b.id !== bundleToDelete.value;

            return !(
              b.amount === bundleToDelete.amount &&
              (b.note || "") === (bundleToDelete.note || "")
            );
          });

          const updatedBundles = updatedBundlesRaw.map(
            ({ companyId, ...rest }) => rest
          );
          setAction(null);
          setBundleToDelete(null);


          try {
            await deleteBundleForCompany(updatedBundles);
          } catch (e) {
            toast.error(messageFromError(e));
          }
        }}
        active={action === "deleteBundle"}
        onCancel={() => {
          setAction(null);
          setBundleToDelete(null);
        }}
        title={t("creditManagement.confirmDeleteTitle")}
      >
        {bundleToDelete && (
          <Trans
            i18nKey="creditManagement.deleteConfirmation"
            values={{ label: bundleToDelete.label }}
            components={{
              strongRed: <strong style={{ color: "red" }} />,
              strong: <strong />,
            }}
          />
        )}
      </ActionModal>

      <ActionModal
        func={async () => {
          if (!bundleToToggle) return;

          const { bundle, active } = bundleToToggle;
          const updatedBundlesRaw = bundles.map((b) => {
            const isMatch = b.id ? b.id === bundle.value : null;

            return isMatch ? { ...b, active } : b;
          });

          const updatedBundles = updatedBundlesRaw.map(
            ({ companyId, ...rest }) => rest
          );
          setAction(null);
          setBundleToToggle(null);

          try {
            await updateBundleVisibilityForCompany(updatedBundles);
            toast.success(
              t("creditManagement.bundleToggleSuccess", {
                action: active
                  ? t("creditManagement.shown")
                  : t("creditManagement.hidden"),
              })
            );
          } catch (e) {
            toast.error(messageFromError(e));
          }
        }}
        active={action === "toggleBundleVisibility"}
        onCancel={() => {
          setAction(null);
          setBundleToToggle(null);
        }}
        title={t("creditManagement.confirmToggleTitle", {
          action: bundleToToggle?.active
            ? t("creditManagement.showing")
            : t("creditManagement.hiding"),
        })}
      >
        {bundleToToggle && (
          <Trans
            i18nKey="creditManagement.toggleConfirmation"
            values={{
              label: bundleToToggle.bundle.label,
              action: bundleToToggle.active ? "show" : "hide",
            }}
            components={{
              strongColor: (
                <strong
                  style={{ color: bundleToToggle.active ? "green" : "orange" }}
                />
              ),
              strong: <strong />,
            }}
          />
        )}
      </ActionModal>

      <ActionModal
        func={async () => {
          setDefaultTemporaryBundleId(null);
          patchBillingInfo();
          setShowConfirmNewDefaultBundle(false);
        }}
        active={showConfirmNewDefaultBundle}
        onCancel={async () => {
          setDefaultBundleId(defaultTemporaryBundleId);
          setDefaultTemporaryBundleId(null);
          setShowConfirmNewDefaultBundle(false);
        }}
        positiveButton={{
          variant: "success",
          text: t("dialogs.yes"),
        }}
        negativeButtonText={t("dialogs.no")}
        title={t("dialogs.confirmDefaultBundle.title")}
      >
        <span>{t("dialogs.confirmDefaultBundle.message")}</span>
      </ActionModal>

      <ActionModal
          func={async () => {
            topUpBalanceForCompany();
          }}
          active={showTopUpConfirmation}
          onCancel={async () => {
            setShowTopUpConfirmation(false)
          }}
          positiveButton={{
            variant: "success",
            text: t("dialogs.confirmTopUpDialog.confirm"),
          }}
          negativeButtonText={t("dialogs.confirmTopUpDialog.cancel")}
          title={t("dialogs.confirmTopUpDialog.title")}
      >
        <span>
            <Trans
                i18nKey="dialogs.confirmTopUpDialog.message"
                values={{ amount: bundles.find((b) => b.id === selectedBundleId)?.amount || 0 }}
                components={{ strong: <strong /> }}
            />
        </span>
      </ActionModal>

      <ActionModal
        func={async () => {
          onConfirmChange();
        }}
        active={showConfirm}
        onCancel={async () => {
          onCancelChange();
        }}
        positiveButton={{
          variant: "success",
          text: t("dialogs.yes") || "Ja",
        }}
        negativeButtonText={t("dialogs.no") || "Nee"}
        title={t("dialogs.confirmWorkdayToggle.title") || "Bevestiging"}
      >
        <span>
          {pendingValue
            ? t("dialogs.confirmWorkdayToggle.enable") ||
              "Weet u zeker dat u de werkdagenmodule aan wilt zetten?"
            : t("dialogs.confirmWorkdayToggle.disable") ||
              "Weet u zeker dat u de werkdagenmodule uit wilt zetten?"}
        </span>
      </ActionModal>
    </div>
  );
};

export default CreditManagementPage;
